/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import type { DealerContextServiceV1, UnsubscribeFn } from '@oneaudi/dealer-context-service';
import {
  ContentServiceV1 as ContentService,
  ContentChangeListener,
} from '@volkswagen-onehub/audi-content-service';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { createTrackingService } from '@volkswagen-onehub/oneaudi-os-adapter-tracking';
import i18n from '../i18n';
import { GRAPHQL_GATEWAY_URL, IS_LOCAL_ENV } from './constants/environment';
import GlobalStyles from './styles';
import { COLORS } from './styles/variables';
import VehicleResultsContainer from './components/VehicleResultsContainer';
import { FilteredWormwoodVehiclesQuery } from './types';
import { AppContent, mapContent } from './ContentMapping/contentMapping';
import ErrorBoundary from './components/ErrorBoundary';
import useLocationSearchData from './hooks/useLocationSearchData';
import { setLocation } from './redux/locationReducer';
import useFilterResults from './hooks/useFilterResults';
import { setConfig, setMarket, selectAppConfig, listOfDealersType } from './redux/appConfigReducer';
import getMarket from './utils/getMarket';
import {
  setFiltersFromLS,
  addOrRemoveTempFilter,
  setMainDealerId,
  selectFilters,
} from './redux/filtersReducer';
import { setVendorCTAs } from './redux/vendorCTAsReducer';
import { getEnabledVendorCTAs } from './utils/getEnabledVendorCTAs';
import { setPriceBreakdowns } from './redux/priceBreakdownReducer';
import {
  filterInitialState,
  resultsReturnedFailed,
  tier3ListOfDealers,
} from './utils/analyticsEventListeners';

import mockContent from './sample-vendorCTA-content.json';
import mockPriceBreakdown from './sample-priceBreakdownContent.json';
import { TIER_3_CONFIG } from './constants/mockVehicleForTesting';
import { setInitialSortBy } from './redux/sortByReducer';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  trackingService?: TrackingServiceV2;
  dealerContextService: DealerContextServiceV1;
  remoteConfig: any;
  contentService?: ContentService;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  // @ts-ignore
  trackingService = createTrackingService(__FEATURE_APP_NAME__),
  remoteConfig,
  dealerContextService,
  contentService,
  ...inherited
}: FeatureAppProps) => {
  const dispatch = useDispatch();

  const [rawContent, setRawContent] = useState(contentService?.getContent() as AppContent);

  console.log('FA_VLP_LIST_PAGE => raw remote config:', remoteConfig);

  const configFile = IS_LOCAL_ENV ? TIER_3_CONFIG : remoteConfig;

  // Get location from LS
  const locationSearchData = useLocationSearchData();

  // Get filters from LS
  const { parsedFilters, parsedConfig, parsedParams } = useFilterResults(configFile);

  // ----------------------------------------------------------------------------------------------
  // Regular useEffects, mostly for T1\

  // Set location to Redux
  useEffect(() => {
    if (!locationSearchData) return;
    dispatch(setLocation(locationSearchData));
  }, [dispatch, locationSearchData]);

  // Set filters to redux
  useEffect(() => {
    if (parsedConfig?.isTier3page) {
      // On Tier3 pages priority is with url params but if there is no params we'll grab it from config
      const filters = parsedParams || parsedConfig.tier3Filters;
      dispatch(setFiltersFromLS(filters));
      filterInitialState(filters);
      return;
    }
    if (!parsedFilters) return;
    dispatch(setFiltersFromLS(parsedFilters));
  }, [
    dispatch,
    parsedConfig?.isTier3page,
    parsedConfig?.tier3Filters,
    parsedFilters,
    parsedParams,
  ]);

  useEffect(() => {
    if (!parsedFilters) return;
    if (parsedConfig?.isTier3page) {
      filterInitialState(parsedConfig.tier3Filters);
      return;
    }
    filterInitialState(parsedFilters);
    // Disabling this dependency, since this function should be called only ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default sortBy for Tier3 pages
  useEffect(() => {
    const sortByFromLS = localStorage.getItem('sortBy');
    if (sortByFromLS && !remoteConfig?.isTier3page) {
      dispatch(setInitialSortBy(JSON.parse(sortByFromLS)));
      return;
    }

    // To test Locally
    if (TIER_3_CONFIG?.isTier3page && IS_LOCAL_ENV) {
      dispatch(setInitialSortBy('DATE_PREDATEEND:ASC'));
      return;
    }
    if (remoteConfig?.isTier3page) {
      dispatch(setInitialSortBy('DATE_PREDATEEND:ASC'));
    }
  }, [dispatch, remoteConfig?.isTier3page]);

  // ----------------------------------------------------------------------------------------------
  // CONFIG useEffect

  // Set config to redux
  // IXIS event if this is T3 page - sending then config data
  useEffect(() => {
    const market = getMarket();

    // just passing market if NO remote config or Tier3 is false(parsedConfig is null)
    if (!configFile?.isTier3page) {
      dispatch(setMarket(configFile.marketType));
      return;
    }

    if (!remoteConfig || isEmpty(remoteConfig) || !parsedConfig) {
      dispatch(setMarket(market));
      return;
    }

    const mainDealer = parsedConfig?.listOfDealers?.find(
      (dealer: listOfDealersType) => dealer?.isMainDealer,
    )?.dealerId;
    tier3ListOfDealers(remoteConfig);
    dispatch(setConfig(parsedConfig));
    dispatch(setMainDealerId(mainDealer));
  }, [configFile?.isTier3page, configFile.marketType, dispatch, parsedConfig, remoteConfig]);

  // ----------------------------------------------------------------------------------------------

  // ----------------------------------------------------------------------------------------------
  // CONTENT useEffects. Mostly related to T3 pages

  const content = mapContent(rawContent);
  contentService?.onContentChange(setRawContent as ContentChangeListener);

  useEffect(() => {
    if (content?.vendorCtaGroup?.length <= 0) return;
    // eslint-disable-next-line
    if (!content.vendorCtaGroup && location.hostname === 'localhost') {
      // data for working in local
      // eslint-disable-next-line
      const mockedEnabledCTAsOnly = getEnabledVendorCTAs(mockContent as any);

      dispatch(setVendorCTAs(mockedEnabledCTAsOnly));
      return;
    }

    const enabledCTAsOnly = getEnabledVendorCTAs(content);
    dispatch(setVendorCTAs(enabledCTAsOnly));
  }, [dispatch, content]);

  useEffect(() => {
    if (!content && Object.keys(content).length === 0) return;

    // eslint-disable-next-line
    if (!content.priceBreakdownGroup && location.hostname === 'localhost') {
      // data for working in local
      dispatch(setPriceBreakdowns(mockPriceBreakdown));
      return;
    }

    dispatch(setPriceBreakdowns(content));
  }, [dispatch, content]);

  // ----------------------------------------------------------------------------------------------

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    trackingService.featureAppName = __FEATURE_APP_NAME__;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, [trackingService]);

  return (
    <I18nextProvider i18n={i18n}>
      {/* @ts-ignore:next-line */}
      <ErrorBoundary>
        <ThemeProvider theme={COLORS}>
          <div className="resetWrapper">
            <VehicleResultsContainer />
          </div>
          <GlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
